import z from "zod";
import { ButtonActionSchema } from "./actions";

const standardisedColors = z.enum(["error", "primary", "secondary", "success", "info", "warning", "neutral"]);
const standardisedVariants = z.enum(["solid", "outline", "soft", "subtle", "ghost", "link"]);
const standardisedSizes = z.enum(["xs", "sm", "md", "lg", "xl"]);
const standardisedFontSizes = z.enum(["xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl"]);

function makeResponsive<T extends z.ZodTypeAny>(schema: T) {
  return z.union([
    schema,
    z.record(z.string(), schema)
  ]);
}

export const ReaderLayoutModalPropsSchema = z.object({
    title: z.string()
});
export type ReaderLayoutModalProps = z.infer<typeof ReaderLayoutModalPropsSchema>;

export const ReaderLayoutContainerPropsSchema = z.object({
  layout: z.enum(["horizontal", "vertical"]).optional(),
  gap: z.number().min(0).max(30).optional(),
  height: z.enum(['full']).optional(),
  width: z.enum(['full']).optional(),
  margin: z.string().regex(/^(?:\d+px|auto)(,\s*(?:\d+px|auto)){3}$/).optional(),
  padding: z.string().regex(/^(?:\d+px|auto)(,\s*(?:\d+px|auto)){3}$/).optional(),
  overflowX: z.enum(['overflow-x-scroll', 'overflow-x-auto']).optional(),
  overflowY: z.enum(['overflow-y-scroll', 'overflow-y-auto']).optional(),
  justifyItems: z.enum([
    "justify-items-start",
    "justify-items-end",
    "justify-items-end-safe",
    "justify-items-center",
    "justify-items-center-safe",
    "justify-items-stretch",
    "justify-items-normal"
  ]).optional(),
  alignItems: z.enum([
    "items-start",
    "items-end",
    "items-end-safe",
    "items-center",
    "items-center-safe",
    "items-baseline",
    "items-baseline-last",
    "items-stretch"
  ]).optional(),
});
export const ResponsiveReaderLayoutContainerPropsSchema = makeResponsive(ReaderLayoutContainerPropsSchema);
export type ReaderLayoutContainerProps = z.infer<typeof ReaderLayoutContainerPropsSchema>;


export const ReaderLayoutLinkPropsSchema = z.object({
  to: z.string().default(""),
  target: z.enum(["_blank", "_parent", "_self", "_top"]).default("_blank")
});
export type ReaderLayoutLinkProps = z.infer<typeof ReaderLayoutLinkPropsSchema>;


export const ReaderLayoutTableOfContentsSchema = z.object({
    color: standardisedColors.default("primary"),
    size: standardisedSizes.default("md"),
    trailingIcon: z.object({
        icon: z.string(),
        size: z.number().min(2).max(14).default(5),
    }).optional(),
})
export type ReaderLayoutTableOfContentsProps = z.infer<typeof ReaderLayoutTableOfContentsSchema>;


export const ReaderLayoutBookmarksSchema = z.object({});
export type ReaderLayoutBookmarksProps = z.infer<typeof ReaderLayoutBookmarksSchema>;


export const ReaderLayoutHighlightsSchema = z.object({});
export type ReaderLayoutHighlightsProps = z.infer<typeof ReaderLayoutHighlightsSchema>;


export const ReaderLayoutSettingsSchema = z.object({});
export type ReaderLayoutSettingsProps = z.infer<typeof ReaderLayoutSettingsSchema>;



export const ReaderLayoutProgressSliderSchema = z.object({
    color: standardisedColors.default("primary"),
    size: standardisedSizes.default("md"),
    orientation: z.enum(["horizontal", "vertical"]).default("horizontal"),
})
export type ReaderLayoutProgressSliderProps = z.infer<typeof ReaderLayoutProgressSliderSchema>;


export const GlobalButtonProps = z.object({
    text: z.string().optional(),
    color: standardisedColors.default("primary"),
    activeColor: standardisedColors.optional(),
    variant: standardisedVariants.default("solid"),
    activeVariant: standardisedVariants.optional(),
    size: standardisedSizes.default("md"),
    fontsize: standardisedFontSizes.default("base"),
    leadingIcon: z.object({
        icon: z.string(),
        size: z.number().min(2).max(14).default(5),
    }).optional(),
    trailingIcon: z.object({
        icon: z.string(),
        size: z.number().min(2).max(14).default(5),
    }).optional(),
    arialabel: z.string().optional(),
    tabindex: z.number().min(-1).optional(),
})

export const ReaderLayoutButtonPropsSchema = GlobalButtonProps.extend({
  action: z.object({
    type: ButtonActionSchema,
    target: z.string().optional(),
  }).optional(),
});
export type ReaderLayoutButtonProps = z.infer<typeof ReaderLayoutButtonPropsSchema>;

export const ReaderLayoutReadAloudButtonPropsSchema = GlobalButtonProps.extend({});
export type ReaderLayoutReadAloudButtonProps = z.infer<typeof ReaderLayoutReadAloudButtonPropsSchema>;

export const ReaderLayoutImagePropsSchema = z.object({
    src: z.union([
        z.string().url(),
        z.enum(["logo"])
    ]),
    alt: z.string().optional(),
    fit: z.enum(["cover", "contain", "fill", "inside", "outside"]).default("cover"),
    width: z.union([
        z.number().min(0),
        z.literal("auto")
    ]).default("auto"),
    height: z.union([
        z.number().min(0),
        z.literal("auto")
    ]).default("auto"),
});
export type ReaderLayoutImageProps = z.infer<typeof ReaderLayoutImagePropsSchema>;


export const ReaderLayoutLabelPropsSchema = z.object({
    flex: z.union([
        z.number().min(0).max(20), 
        z.literal("auto")
    ]).optional(),
    padding: z.string().regex(/^\d+px(,\s*\d+px){3}$/).optional(),
    text: z.union([
        z.string().startsWith("$"),
        z.string()
    ]).optional(),
    fontsize: standardisedFontSizes.default("base"),
    truncate: z.boolean().default(false),
});
export type ReaderLayoutLabelProps = z.infer<typeof ReaderLayoutLabelPropsSchema>;


export const ReaderLayoutInputPropsSchema = z.object({
    type: z.enum(["number", "text", "search", "checkbox", "date", "email", "hidden", "radio", "range", "tel", "url"]).default("text"),
    placeholder: z.string().optional(),
    size:  standardisedSizes.default("md"),
    required: z.boolean().default(false)
});
export type ReaderLayoutInputProps = z.infer<typeof ReaderLayoutInputPropsSchema>;


export const ReaderLayoutToastPropsSchema = z.object({
    position: z.enum(["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"]).default("top-right"),
    color: standardisedColors.default("primary"),
    progress: z.boolean().default(true),
    duration: z.number().min(1000).max(10000).default(3000),
});
export type ReaderLayoutToastProps = z.infer<typeof ReaderLayoutToastPropsSchema>;